/**
 * Bundle Builder — Kinnder
 * File: assets/bundle-builder.js
 *
 * Vanilla JS, no dependencies.
 * Supports steps 1–5 (steps 3, 4 & 5 optional), two separate personalisation
 * steps (backpack + lunch bag, each independently optional), Monster Cart.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────
     Colour name → hex swatch fallback map
  ───────────────────────────────────────── */
  var SWATCH_MAP = {
    'dusty pink':  '#E8A4B0', 'pink':        '#E8A4B0', 'hot pink':    '#E0548A',
    'sage':        '#8FB4A0', 'sage green':  '#8FB4A0', 'green':       '#7DAF88',
    'forest':      '#3D7A5A', 'sky blue':    '#8BB8D4', 'blue':        '#7BA8C8',
    'navy':        '#2B4A7A', 'lilac':       '#C4A8D4', 'purple':      '#9B7EC8',
    'lavender':    '#C4A8D4', 'white':       '#F5F5F5', 'cream':       '#F5EDE0',
    'natural':     '#D4C4A0', 'black':       '#2A2A2A', 'charcoal':    '#5A5A5A',
    'grey':        '#B0B0B0', 'gray':        '#B0B0B0', 'caramel':     '#D4A567',
    'tan':         '#D4A567', 'brown':       '#9A6B4A', 'red':         '#D4534A',
    'coral':       '#E8846A', 'orange':      '#E89A4A', 'yellow':      '#E8D467',
    'teal':        '#4AB5A8', 'mint':        '#8FD4C0'
  };

  /* ─────────────────────────────────────────
     Utility helpers
  ───────────────────────────────────────── */

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatMoney(cents) {
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  /* product.js → integer cents; collections products.json → decimal string */
  function toCents(price) {
    if (typeof price === 'number') return Math.round(price);
    return Math.round(parseFloat(price) * 100);
  }

  function getSwatchColour(name) {
    return SWATCH_MAP[String(name).toLowerCase().trim()] || '#D0D0D0';
  }

  function thumbUrl(src) {
    if (!src) return null;
    return src.replace(/(\.[^.?]+)(\?.*)?$/, '_320x320_crop_center$1$2');
  }

  /* ─────────────────────────────────────────
     Fetch helpers
  ───────────────────────────────────────── */

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .catch(function (e) { console.warn('[BundleBuilder] Could not fetch product "' + handle + '":', e); return null; });
  }

  function fetchCollection(handle) {
    if (!handle) return Promise.resolve(null);
    return fetch('/collections/' + encodeURIComponent(handle) + '/products.json?limit=50')
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) { return (data && data.products && data.products.length) ? data.products : null; })
      .catch(function (e) { console.warn('[BundleBuilder] Could not fetch collection "' + handle + '":', e); return null; });
  }

  /**
   * Normalise fetch result into a flat array of selectable items:
   * { id, title, price (cents), available, imageUrl }
   */
  function normalise(type, data) {
    if (!data) return [];

    if (type === 'collection') {
      var items = [];
      data.forEach(function (product) {
        var variant = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].available) { variant = product.variants[i]; break; }
        }
        if (!variant) variant = product.variants[0];
        if (!variant) return;

        var imgSrc = null;
        if (product.featured_image) {
          imgSrc = thumbUrl(typeof product.featured_image === 'string'
            ? product.featured_image : product.featured_image.src);
        } else if (product.images && product.images.length) {
          imgSrc = thumbUrl(product.images[0].src);
        }
        items.push({ id: variant.id, inventoryItemId: variant.inventory_item_id || null, title: product.title, price: toCents(variant.price), available: !!variant.available, imageUrl: imgSrc });
      });
      return items;
    }

    /* type === 'product': deduplicate by colour option */
    var product  = data;
    var colourIdx = -1;
    if (product.options) {
      product.options.forEach(function (opt, i) {
        var n = opt.toLowerCase();
        if (n === 'colour' || n === 'color' || n.indexOf('colour') !== -1 || n.indexOf('color') !== -1) colourIdx = i;
      });
    }
    var seen = {}, out = [];
    product.variants.forEach(function (v) {
      var colourName = colourIdx >= 0 ? v['option' + (colourIdx + 1)] : v.title;
      if (seen[colourName]) return;
      seen[colourName] = true;
      var imgSrc = v.featured_image
        ? thumbUrl(v.featured_image.src)
        : (product.images && product.images.length ? thumbUrl(product.images[0].src) : null);
      out.push({ id: v.id, inventoryItemId: v.inventory_item_id || null, title: colourName, price: toCents(v.price), available: !!v.available, imageUrl: imgSrc });
    });
    return out;
  }

  /* ─────────────────────────────────────────
     BundleBuilder — one instance per section
  ───────────────────────────────────────── */

  function BundleBuilder(rootEl) {
    var sid = rootEl.dataset.sectionId;
    var d   = rootEl.dataset;

    /* Config */
    var cfg = {
      sid:          sid,
      productVarId: parseInt(d.productVariantId, 10),
      productPrice: parseInt(d.productPrice, 10),
      /* Steps 1 & 2 — always required */
      step1Type:    d.step1Type   || 'collection',
      step1Handle:  d.step1Handle || '',
      step1Label:   d.step1Label  || 'Choose your backpack',
      step2Type:    d.step2Type   || 'collection',
      step2Handle:  d.step2Handle || '',
      step2Label:   d.step2Label  || 'Choose your lunch bag',
      /* Step 3 — optional extra */
      step3Enabled:  d.step3Enabled  === 'true',
      step3Type:     d.step3Type     || 'collection',
      step3Handle:   d.step3Handle   || '',
      step3Label:    d.step3Label    || 'Choose your item',
      step3Required: d.step3Required !== 'false',
      /* Step 4 — optional extra */
      step4Enabled:  d.step4Enabled  === 'true',
      step4Type:     d.step4Type     || 'collection',
      step4Handle:   d.step4Handle   || '',
      step4Label:    d.step4Label    || 'Choose your item',
      step4Required: d.step4Required !== 'false',
      /* Step 5 — optional extra */
      step5Enabled:  d.step5Enabled  === 'true',
      step5Type:     d.step5Type     || 'collection',
      step5Handle:   d.step5Handle   || '',
      step5Label:    d.step5Label    || 'Choose your item',
      step5Required: d.step5Required !== 'false',
      /* Discount */
      discount:     parseInt(d.discount, 10) || 0,
      /* Personalisation 1 — backpack */
      persEnabled:  d.persEnabled === 'true',
      persLabel:    d.persLabel   || 'Personalise your backpack',
      persPrice:    Math.round((parseFloat(d.persPrice) || 0) * 100),
      persHandle:   d.persHandle  || '',
      persLimit:    parseInt(d.persLimit, 10) || 9,
      /* Personalisation 2 — lunch bag */
      pers2Enabled: d.pers2Enabled === 'true',
      pers2Label:   d.pers2Label   || 'Personalise your lunch bag',
      pers2Price:   Math.round((parseFloat(d.pers2Price) || 0) * 100),
      pers2Handle:  d.pers2Handle  || '',
      pers2Limit:   parseInt(d.pers2Limit, 10) || 9
    };

    var step3Active   = cfg.step3Enabled && !!cfg.step3Handle;
    var step4Active   = cfg.step4Enabled && !!cfg.step4Handle;
    var step5Active   = cfg.step5Enabled && !!cfg.step5Handle;
    /* Pers step numbers sit after all product steps */
    var pers1StepNum  = 2 + (step3Active ? 1 : 0) + (step4Active ? 1 : 0) + (step5Active ? 1 : 0) + 1;
    var pers2StepNum  = pers1StepNum + (cfg.persEnabled ? 1 : 0);

    /* State */
    var state = {
      step1: null, step2: null, step3: null, step4: null, step5: null,
      pers:        null, persSkipped:  false, persVarId:  null,  /* pers1 — backpack  */
      pers2:       null, pers2Skipped: false, pers2VarId: null   /* pers2 — lunch bag */
    };

    var persWarnTimer  = null;
    var pers2WarnTimer = null;

    /* ── Bootstrap ─────────────────────────── */

    var fetchStep3 = step3Active
      ? (cfg.step3Type === 'collection' ? fetchCollection(cfg.step3Handle) : fetchProduct(cfg.step3Handle))
      : Promise.resolve(null);
    var fetchStep4 = step4Active
      ? (cfg.step4Type === 'collection' ? fetchCollection(cfg.step4Handle) : fetchProduct(cfg.step4Handle))
      : Promise.resolve(null);
    var fetchStep5 = step5Active
      ? (cfg.step5Type === 'collection' ? fetchCollection(cfg.step5Handle) : fetchProduct(cfg.step5Handle))
      : Promise.resolve(null);
    var fetchPers = (cfg.persEnabled && cfg.persHandle)
      ? fetchProduct(cfg.persHandle)
      : Promise.resolve(null);
    var fetchPers2 = (cfg.pers2Enabled && cfg.pers2Handle)
      ? fetchProduct(cfg.pers2Handle)
      : Promise.resolve(null);

    var fetchStep1 = cfg.step1Type === 'collection' ? fetchCollection(cfg.step1Handle) : fetchProduct(cfg.step1Handle);
    var fetchStep2 = cfg.step2Type === 'collection' ? fetchCollection(cfg.step2Handle) : fetchProduct(cfg.step2Handle);

    Promise.all([fetchStep1, fetchStep2, fetchStep3, fetchStep4, fetchStep5, fetchPers, fetchPers2])
      .then(function (results) {
        var raw1 = results[0], raw2 = results[1], raw3 = results[2],
            raw4 = results[3], raw5 = results[4], pp  = results[5], pp2 = results[6];

        var items1 = normalise(cfg.step1Type, raw1);
        var items2 = normalise(cfg.step2Type, raw2);
        var items3 = step3Active ? normalise(cfg.step3Type, raw3) : [];
        var items4 = step4Active ? normalise(cfg.step4Type, raw4) : [];
        var items5 = step5Active ? normalise(cfg.step5Type, raw5) : [];

        if (!items1.length || !items2.length) {
          console.error('[BundleBuilder] No items for required steps. Check handles in theme settings.');
          showError();
          return;
        }

        if (pp  && pp.variants  && pp.variants.length)  state.persVarId  = pp.variants[0].id;
        if (pp2 && pp2.variants && pp2.variants.length)  state.pers2VarId = pp2.variants[0].id;

        buildUI(items1, items2, items3, items4, items5);
        showContent();
      })
      .catch(function (e) { console.error('[BundleBuilder] Bootstrap error:', e); showError(); });

    /* ── Show / hide panels ────────────────── */

    function showContent() {
      byId('bb-loading-' + sid).style.display = 'none';
      byId('bb-content-' + sid).style.display = 'block';
    }
    function showError() {
      byId('bb-loading-' + sid).style.display = 'none';
      byId('bb-error-'   + sid).style.display = 'block';
    }

    /* ── Build UI ──────────────────────────── */

    function buildUI(items1, items2, items3, items4, items5) {
      var container = byId('bb-steps-' + sid);

      container.appendChild(buildStep(1, cfg.step1Label, items1, true));
      container.appendChild(buildStep(2, cfg.step2Label, items2, true));
      if (step3Active && items3.length) container.appendChild(buildStep(3, cfg.step3Label, items3, cfg.step3Required));
      if (step4Active && items4.length) container.appendChild(buildStep(4, cfg.step4Label, items4, cfg.step4Required));
      if (step5Active && items5.length) container.appendChild(buildStep(5, cfg.step5Label, items5, cfg.step5Required));
      if (cfg.persEnabled)  container.appendChild(buildPersStep(pers1StepNum, 1));
      if (cfg.pers2Enabled) container.appendChild(buildPersStep(pers2StepNum, 2));

      open('bb-body-step1-' + sid);
      byId('bb-atc-' + sid).addEventListener('click', function (e) { e.preventDefault(); handleAddToCart(); });
      updateSummary();
    }

    /* ── Step builder ──────────────────────── */

    function buildStep(stepNum, label, items, required) {
      var wrap = make('div', { className: 'bb-step', id: 'bb-step' + stepNum + '-' + sid });

      var head = make('div', { className: 'bb-step-head' });
      head.addEventListener('click', function () { toggleBody(stepNum); });
      head.innerHTML = [
        '<div class="bb-step-num" id="bb-num-step' + stepNum + '-' + sid + '">' + stepNum + '</div>',
        '<div class="bb-step-info">',
          '<p class="bb-step-name">' + escHtml(label) + '</p>',
          '<p class="bb-step-meta" id="bb-meta-step' + stepNum + '-' + sid + '">Select 1 to continue</p>',
        '</div>',
        required
          ? '<span class="bb-req-badge">Required</span>'
          : '<span class="bb-opt-badge">Optional</span>'
      ].join('');

      var body       = make('div', { className: 'bb-step-body', id: 'bb-body-step' + stepNum + '-' + sid });
      var scrollWrap = make('div', { className: 'bb-cards-scroll-wrap' });
      var cards      = make('div', { className: 'bb-cards' });

      items.forEach(function (item) { cards.appendChild(buildCard(stepNum, item)); });

      var btnPrev = make('button', { className: 'bb-scroll-btn bb-scroll-btn--prev', type: 'button' });
      var btnNext = make('button', { className: 'bb-scroll-btn bb-scroll-btn--next', type: 'button' });
      btnPrev.innerHTML = '&#8249;';
      btnNext.innerHTML = '&#8250;';

      btnPrev.addEventListener('click', function (e) { e.stopPropagation(); cards.scrollBy({ left: -330, behavior: 'smooth' }); });
      btnNext.addEventListener('click', function (e) { e.stopPropagation(); cards.scrollBy({ left:  330, behavior: 'smooth' }); });

      function updateArrows() {
        btnPrev.style.display = cards.scrollLeft <= 4 ? 'none' : 'flex';
        btnNext.style.display = cards.scrollLeft >= cards.scrollWidth - cards.clientWidth - 4 ? 'none' : 'flex';
      }
      cards.addEventListener('scroll', updateArrows);
      setTimeout(updateArrows, 80);

      scrollWrap.appendChild(btnPrev);
      scrollWrap.appendChild(cards);
      scrollWrap.appendChild(btnNext);
      body.appendChild(scrollWrap);
      wrap.appendChild(head);
      wrap.appendChild(body);
      return wrap;
    }

    /* ── Card builder ──────────────────────── */

    function buildCard(stepNum, item) {
      var card = make('div', {
        className: 'bb-card' + (item.available ? '' : ' bb-card--soldout')
      });
      card.dataset.itemId = item.id;

      var imgStyle = item.imageUrl
        ? 'background-image:url(' + item.imageUrl + ');'
        : 'background:' + getSwatchColour(item.title) + ';';

      var priceHtml = item.available
        ? '<div class="bb-card-price">' + formatMoney(item.price) + '</div>'
        : '<div class="bb-card-soldout">Sold out</div>';

      card.innerHTML = [
        '<div class="bb-card-img" style="' + imgStyle + '" aria-hidden="true"></div>',
        '<div class="bb-card-info">',
          '<div class="bb-card-name">' + escHtml(item.title) + '</div>',
          priceHtml,
        '</div>'
      ].join('');

      if (item.available) {
        card.addEventListener('click', function () { selectItem(stepNum, item); });
      }
      return card;
    }

    /* ── Personalisation step builder ─────── */
    /*
     * persIdx = 1 → backpack personalisation  (sNum = pers1StepNum)
     * persIdx = 2 → lunch bag personalisation (sNum = pers2StepNum)
     * All element IDs are scoped to persIdx so both steps can coexist.
     */
    function buildPersStep(sNum, persIdx) {
      var p       = persIdx === 2 ? 'pers2' : 'pers1';  /* id prefix */
      var label   = persIdx === 2 ? cfg.pers2Label  : cfg.persLabel;
      var price   = persIdx === 2 ? cfg.pers2Price  : cfg.persPrice;
      var limit   = persIdx === 2 ? cfg.pers2Limit  : cfg.persLimit;
      var hint    = persIdx === 2
        ? 'Add embroidery to your lunch bag'
        : 'Embroider a name on your backpack';

      var wrap = make('div', { className: 'bb-step', id: 'bb-step' + sNum + '-' + sid });

      var head = make('div', { className: 'bb-step-head' });
      head.addEventListener('click', function () { toggleBody(sNum); });
      head.innerHTML = [
        '<div class="bb-step-num" id="bb-num-step' + sNum + '-' + sid + '">' + sNum + '</div>',
        '<div class="bb-step-info">',
          '<p class="bb-step-name">' + escHtml(label) + '</p>',
          '<p class="bb-step-meta" id="bb-meta-step' + sNum + '-' + sid + '">' + hint + '</p>',
        '</div>',
        '<span class="bb-opt-badge">Optional &middot; +' + formatMoney(price) + '</span>'
      ].join('');

      var body = make('div', { className: 'bb-step-body', id: 'bb-body-step' + sNum + '-' + sid });
      body.innerHTML = [
        '<div class="bb-pers-box">',
          '<div class="bb-pers-price-row">',
            '<span class="bb-pers-label">Name embroidery</span>',
            '<span class="bb-pers-price-tag">+ ' + formatMoney(price) + '</span>',
          '</div>',
          '<p class="bb-pers-hint">Capital letters only &middot; ' + limit + ' characters max &middot; spaces count</p>',
          '<div class="bb-pers-input-wrap">',
            '<input class="bb-pers-input" type="text" id="bb-' + p + '-input-' + sid + '"',
              ' maxlength="' + limit + '"',
              ' placeholder="E.G. CHARLOTTE"',
              ' autocomplete="off" autocorrect="off" autocapitalize="characters" spellcheck="false">',
            '<span class="bb-pers-counter" id="bb-' + p + '-counter-' + sid + '">0/' + limit + '</span>',
          '</div>',
          '<p class="bb-pers-warn" id="bb-' + p + '-warn-' + sid + '">Only capital letters and spaces are allowed.</p>',
          '<div class="bb-pers-preview" id="bb-' + p + '-preview-' + sid + '">',
            '<span style="color:var(--bb-accent)" aria-hidden="true">&#9679;</span>',
            '<span>Embroidered: <span class="bb-emb-name" id="bb-' + p + '-emb-name-' + sid + '"></span></span>',
          '</div>',
          '<p class="bb-pers-note">Personalised items cannot be returned or exchanged. Please check your spelling carefully — embroidery cannot be changed once your order is placed.</p>',
          '<button class="bb-skip-link" id="bb-' + p + '-skip-' + sid + '">No thanks — skip personalisation</button>',
        '</div>'
      ].join('');

      wrap.appendChild(head);
      wrap.appendChild(body);

      setTimeout(function () {
        var input = byId('bb-' + p + '-input-' + sid);
        if (input) input.addEventListener('input', function () { handlePersInput(this, persIdx, sNum); });
        var skip = byId('bb-' + p + '-skip-' + sid);
        if (skip) skip.addEventListener('click', function () { handleSkipPers(persIdx, sNum); });
      }, 0);

      return wrap;
    }

    /* ── Selection handlers ────────────────── */

    function selectItem(stepNum, item) {
      state['step' + stepNum] = { variantId: item.id, inventoryItemId: item.inventoryItemId || null, title: item.title, price: item.price };

      var body = byId('bb-body-step' + stepNum + '-' + sid);
      body.querySelectorAll('.bb-card').forEach(function (c) { c.classList.remove('bb-card--selected'); });
      var target = body.querySelector('[data-item-id="' + item.id + '"]');
      if (target) target.classList.add('bb-card--selected');

      markDone('bb-num-step' + stepNum + '-' + sid);
      setText('bb-meta-step' + stepNum + '-' + sid, item.title + ' selected');

      var nextId = 'bb-body-step' + (stepNum + 1) + '-' + sid;
      if (byId(nextId)) setTimeout(function () { open(nextId); }, 260);

      updateSummary();
    }

    /* persIdx: 1 = backpack, 2 = lunch bag; sNum = the step's display number */
    function handlePersInput(input, persIdx, sNum) {
      var p       = persIdx === 2 ? 'pers2' : 'pers1';
      var stateKey = persIdx === 2 ? 'pers2' : 'pers';
      var warnTimer = persIdx === 2 ? pers2WarnTimer : persWarnTimer;

      var raw   = input.value;
      var clean = raw.toUpperCase().replace(/[^A-Z ]/g, '');
      if (clean !== raw) {
        input.value = clean;
        var w = byId('bb-' + p + '-warn-' + sid);
        if (w) {
          w.classList.add('show');
          clearTimeout(warnTimer);
          var t = setTimeout(function () { w.classList.remove('show'); }, 2500);
          if (persIdx === 2) pers2WarnTimer = t; else persWarnTimer = t;
        }
      }

      var len     = clean.length;
      var limit   = persIdx === 2 ? cfg.pers2Limit : cfg.persLimit;
      var counter = byId('bb-' + p + '-counter-' + sid);
      if (counter) {
        counter.textContent = len + '/' + limit;
        counter.className   = 'bb-pers-counter' + (len >= limit - 1 ? ' warn' : '');
      }

      var preview = byId('bb-' + p + '-preview-' + sid);
      var embName = byId('bb-' + p + '-emb-name-' + sid);
      var numEl   = byId('bb-num-step' + sNum + '-' + sid);
      var metaEl  = byId('bb-meta-step' + sNum + '-' + sid);
      var price   = persIdx === 2 ? cfg.pers2Price : cfg.persPrice;

      if (len > 0) {
        state[stateKey] = clean;
        if (persIdx === 2) state.pers2Skipped = false; else state.persSkipped = false;
        if (embName) embName.textContent = clean;
        if (preview) preview.classList.add('show');
        if (numEl)   { numEl.classList.remove('skipped'); markDone(numEl.id); }
        if (metaEl)  metaEl.textContent = '"' + clean + '" · +' + formatMoney(price);
      } else {
        state[stateKey] = null;
        if (preview) preview.classList.remove('show');
        if (numEl)   { numEl.classList.remove('done', 'skipped'); numEl.textContent = String(sNum); }
        if (metaEl) {
          var hint = persIdx === 2 ? 'Add embroidery to your lunch bag' : 'Embroider a name on your backpack';
          metaEl.textContent = hint;
        }
      }
      updateSummary();
    }

    function handleSkipPers(persIdx, sNum) {
      var p        = persIdx === 2 ? 'pers2' : 'pers1';
      var stateKey = persIdx === 2 ? 'pers2' : 'pers';
      var limit    = persIdx === 2 ? cfg.pers2Limit : cfg.persLimit;

      state[stateKey] = null;
      if (persIdx === 2) state.pers2Skipped = true; else state.persSkipped = true;

      var input   = byId('bb-' + p + '-input-'   + sid);
      var counter = byId('bb-' + p + '-counter-' + sid);
      var preview = byId('bb-' + p + '-preview-' + sid);
      var numEl   = byId('bb-num-step' + sNum + '-' + sid);
      if (input)   input.value = '';
      if (counter) { counter.textContent = '0/' + limit; counter.className = 'bb-pers-counter'; }
      if (preview) preview.classList.remove('show');
      if (numEl)   { numEl.classList.remove('done'); numEl.classList.add('skipped'); numEl.innerHTML = '&minus;'; }
      setText('bb-meta-step' + sNum + '-' + sid, 'Skipped — no personalisation');
      close('bb-body-step' + sNum + '-' + sid);
      updateSummary();
    }

    /* ── Add to cart ───────────────────────── */

    function handleAddToCart() {
      var btn = byId('bb-atc-' + sid);
      if (!btn || btn.disabled) return;
      btn.disabled    = true;
      btn.textContent = 'Adding to cart…';

      /*
       * PRICING: The bundle product (cfg.productVarId) is the single cart line
       * item — it holds the pre-calculated discounted bundle price so Shopify
       * charges the customer the correct amount without any discount code.
       *
       * INVENTORY SYNC: The actual variant IDs of selected component products
       * are stored as hidden line item properties (underscore prefix = hidden
       * from customer-facing cart/checkout/packing slip display).
       * Shopify Flow reads these IDs on order creation and decrements the
       * correct inventory for each component automatically.
       */

      /* Visible properties — shown in cart, checkout, order confirmation & admin */
      var props = {};
      props[labelToKey(cfg.step1Label)] = state.step1.title;
      props[labelToKey(cfg.step2Label)] = state.step2.title;
      if (state.step3) props[labelToKey(cfg.step3Label)] = state.step3.title;
      if (state.step4) props[labelToKey(cfg.step4Label)] = state.step4.title;
      if (state.step5) props[labelToKey(cfg.step5Label)] = state.step5.title;
      if (state.pers)  props['Personalisation (Backpack)'] = state.pers;
      if (state.pers2) props['Personalisation (Lunch Bag)'] = state.pers2;

      /* Hidden variant IDs + inventory item IDs — read by Shopify Flow */
      props['_step1_vid'] = String(state.step1.variantId);
      props['_step2_vid'] = String(state.step2.variantId);
      if (state.step3) props['_step3_vid'] = String(state.step3.variantId);
      if (state.step4) props['_step4_vid'] = String(state.step4.variantId);
      if (state.step5) props['_step5_vid'] = String(state.step5.variantId);

      if (state.step1.inventoryItemId) props['_step1_iid'] = String(state.step1.inventoryItemId);
      if (state.step2.inventoryItemId) props['_step2_iid'] = String(state.step2.inventoryItemId);
      if (state.step3 && state.step3.inventoryItemId) props['_step3_iid'] = String(state.step3.inventoryItemId);
      if (state.step4 && state.step4.inventoryItemId) props['_step4_iid'] = String(state.step4.inventoryItemId);
      if (state.step5 && state.step5.inventoryItemId) props['_step5_iid'] = String(state.step5.inventoryItemId);

      var items = [{
        id:         cfg.productVarId,
        quantity:   1,
        properties: props
      }];

      /* Backpack personalisation as a separate line item */
      if (state.pers && state.persVarId) {
        items.push({
          id:       state.persVarId,
          quantity: 1,
          properties: {
            'Name':    state.pers,
            '_bundle': 'yes',
            '_for':    'Backpack'
          }
        });
      }

      /* Lunch bag personalisation as a separate line item */
      if (state.pers2 && state.pers2VarId) {
        items.push({
          id:       state.pers2VarId,
          quantity: 1,
          properties: {
            'Name':    state.pers2,
            '_bundle': 'yes',
            '_for':    'Lunch Bag'
          }
        });
      }

      if (typeof window.monster_addToCart === 'function') {
        addItemsViaMonster(items, btn);
      } else {
        addItemsViaFetch(items, btn);
      }
    }

    /* Add items via a hidden iframe's native fetch.
       Monster Upsells 2.0 patches BOTH window.fetch AND XMLHttpRequest on the
       top-level window, blocking /cart/add.js calls from external scripts.
       A freshly-created iframe has its own unpatched fetch — we borrow it,
       keep the iframe alive until the request resolves, then clean up.
       After a successful add we call monster_refresh() so Monster Cart syncs
       its drawer from the updated Shopify cart, then open the drawer. */
    function addItemsViaMonster(items, btn) {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      var nativeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);

      nativeFetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json().then(function (d) { return { status: r.status, data: d }; }); })
        .then(function (res) {
          document.body.removeChild(iframe);
          if (res.status === 200 && !res.data.description) {
            btn.textContent = 'Added!';
            if (typeof window.monster_refresh === 'function') window.monster_refresh();
            setTimeout(function () {
              if      (typeof window.openCart   === 'function') window.openCart();
              else if (typeof window.toggleCart === 'function') window.toggleCart();
            }, 450);
          } else {
            console.error('[BundleBuilder] /cart/add.js error:', res.data);
            btn.disabled    = false;
            btn.textContent = 'Add bundle to cart';
            alert('Something went wrong adding to cart. Please try again.');
          }
        })
        .catch(function (err) {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          console.error('[BundleBuilder] iframe fetch error:', err);
          btn.disabled    = false;
          btn.textContent = 'Add bundle to cart';
          alert('Something went wrong adding to cart. Please try again.');
        });
    }

    /* Standard Shopify fetch fallback (used when Monster Cart API is unavailable) */
    function addItemsViaFetch(items, btn) {
      fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body:    JSON.stringify({ items: items })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status === 422 || data.description) throw new Error(data.description || 'Error');
          btn.textContent = 'Added!';
          openCartFallback();
        })
        .catch(function (err) {
          console.error('[BundleBuilder] Add to cart failed:', err);
          btn.disabled    = false;
          btn.textContent = 'Add bundle to cart';
          alert('Something went wrong adding to cart. Please try again.');
        });
    }

    /* Fallback cart opener when Monster Cart API was not used directly */
    function openCartFallback() {
      if (typeof window.openCart    === 'function') { window.openCart();    return; }
      if (typeof window.toggleCart  === 'function') { window.toggleCart();  return; }
      if (typeof window.monster_refresh === 'function') window.monster_refresh();
      document.dispatchEvent(new CustomEvent('cart:refresh',     { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      setTimeout(function () { window.location.href = '/cart'; }, 800);
    }

    /* ── Summary / pricing ─────────────────── */

    function updateSummary() {
      var done1 = !!state.step1;
      var done2 = !!state.step2;
      var done3 = !!state.step3;
      var done4 = !!state.step4;
      var done5 = !!state.step5;

      var allRequiredDone = done1 && done2
        && (!step3Active || !cfg.step3Required || done3)
        && (!step4Active || !cfg.step4Required || done4)
        && (!step5Active || !cfg.step5Required || done5);

      /* Progress bar */
      var totalSteps = 2
        + (step3Active ? 1 : 0)
        + (step4Active ? 1 : 0)
        + (step5Active ? 1 : 0)
        + (cfg.persEnabled  ? 1 : 0)
        + (cfg.pers2Enabled ? 1 : 0);
      var doneSteps = (done1 ? 1 : 0) + (done2 ? 1 : 0) + (done3 ? 1 : 0) + (done4 ? 1 : 0) + (done5 ? 1 : 0)
        + (state.pers  || state.persSkipped  ? 1 : 0)
        + (state.pers2 || state.pers2Skipped ? 1 : 0);
      var prog = byId('bb-progress-' + sid);
      if (prog) prog.style.width = Math.round((doneSteps / totalSteps) * 100) + '%';

      /* Summary row values */
      if (done1) setVal('bb-val-step1-' + sid, formatMoney(state.step1.price));
      if (done2) setVal('bb-val-step2-' + sid, formatMoney(state.step2.price));
      if (done3) setVal('bb-val-step3-' + sid, formatMoney(state.step3.price));
      if (done4) setVal('bb-val-step4-' + sid, formatMoney(state.step4.price));
      if (done5) setVal('bb-val-step5-' + sid, formatMoney(state.step5.price));

      var rowDiscount  = byId('bb-row-discount-'  + sid);
      var valDiscount  = byId('bb-val-discount-'  + sid);
      var rowPers      = byId('bb-row-pers-'      + sid);
      var rowPers2     = byId('bb-row-pers2-'     + sid);
      var totalEl      = byId('bb-total-'         + sid);
      var priceNow     = byId('bb-price-now-'     + sid);
      var priceWas     = byId('bb-price-was-'     + sid);
      var saveBadge    = byId('bb-save-badge-'    + sid);
      var summaryItems = byId('bb-summary-items-' + sid);
      var atcBtn       = byId('bb-atc-'           + sid);
      var atcNote      = byId('bb-atc-note-'      + sid);

      if (allRequiredDone) {
        var subtotal = (done1 ? state.step1.price : 0) + (done2 ? state.step2.price : 0)
          + (done3 ? state.step3.price : 0) + (done4 ? state.step4.price : 0) + (done5 ? state.step5.price : 0);
        var discountAmt = Math.round(subtotal * cfg.discount / 100);
        var cartTotal   = cfg.productPrice
          + (state.pers  ? cfg.persPrice  : 0)
          + (state.pers2 ? cfg.pers2Price : 0);

        show(rowDiscount);
        if (valDiscount) valDiscount.textContent = '−' + formatMoney(discountAmt);
        if (rowPers)     rowPers.style.display   = state.pers  ? 'flex' : 'none';
        if (rowPers2)    rowPers2.style.display  = state.pers2 ? 'flex' : 'none';
        if (totalEl)     totalEl.textContent     = formatMoney(cartTotal) + ' AUD';
        if (priceNow)    priceNow.textContent    = formatMoney(cartTotal) + ' AUD';
        if (priceWas)  { priceWas.textContent    = formatMoney(subtotal); show(priceWas); }
        if (saveBadge) { saveBadge.textContent   = 'Save ' + cfg.discount + '%'; show(saveBadge); }

        var parts = [];
        if (done1) parts.push(state.step1.title + ' ' + simplify(cfg.step1Label));
        if (done2) parts.push(state.step2.title + ' ' + simplify(cfg.step2Label));
        if (done3) parts.push(state.step3.title + ' ' + simplify(cfg.step3Label));
        if (done4) parts.push(state.step4.title + ' ' + simplify(cfg.step4Label));
        if (done5) parts.push(state.step5.title + ' ' + simplify(cfg.step5Label));
        if (state.pers)  parts.push('Backpack embroidery: "' + state.pers + '"');
        if (state.pers2) parts.push('Lunch bag embroidery: "' + state.pers2 + '"');
        if (summaryItems) summaryItems.innerHTML = '<span style="color:var(--bb-heading)">' + escHtml(parts.join(' · ')) + '</span>';

        var hasAnyPers = state.pers || state.pers2;
        if (atcBtn) { atcBtn.disabled = false; atcBtn.setAttribute('aria-disabled', 'false'); atcBtn.textContent = 'Add bundle to cart'; }
        if (atcNote) atcNote.textContent = hasAnyPers ? 'Personalised items cannot be returned or exchanged' : '';

      } else {
        hide(rowDiscount);
        if (rowPers)   rowPers.style.display   = 'none';
        if (rowPers2)  rowPers2.style.display  = 'none';
        if (priceWas)  priceWas.style.display  = 'none';
        if (saveBadge) saveBadge.style.display = 'none';
        if (totalEl)   totalEl.textContent     = formatMoney(cfg.productPrice) + ' AUD';

        var missing = [];
        if (!done1) missing.push(simplify(cfg.step1Label));
        if (!done2) missing.push(simplify(cfg.step2Label));
        if (step3Active && cfg.step3Required && !done3) missing.push(simplify(cfg.step3Label));
        if (step4Active && cfg.step4Required && !done4) missing.push(simplify(cfg.step4Label));
        if (step5Active && cfg.step5Required && !done5) missing.push(simplify(cfg.step5Label));
        if (summaryItems) summaryItems.textContent = 'Still need: ' + missing.join(' and ') + '.';
        if (atcBtn) { atcBtn.disabled = true; atcBtn.setAttribute('aria-disabled', 'true'); atcBtn.textContent = 'Select required items to continue'; }
        if (atcNote) atcNote.textContent = '';
      }
    }

    /* ── DOM helpers ───────────────────────── */

    function byId(id)    { return document.getElementById(id); }
    function make(tag,p) { var el=document.createElement(tag); Object.keys(p||{}).forEach(function(k){el[k]=p[k];}); return el; }
    function open(id)    { var el=byId(id); if(el) el.classList.add('open'); }
    function close(id)   { var el=byId(id); if(el) el.classList.remove('open'); }
    function show(el)    { if(el) el.style.display='flex'; }
    function hide(el)    { if(el) el.style.display='none'; }
    function setText(id,t){ var el=byId(id); if(el) el.textContent=t; }

    function setVal(id, text) {
      var el = byId(id);
      if (!el) return;
      el.className   = 'val';
      el.textContent = text;
    }

    function markDone(id) {
      var el = byId(id);
      if (!el) return;
      el.classList.add('done');
      el.innerHTML = '&#10003;';
    }

    function toggleBody(n) {
      var b = byId('bb-body-step' + n + '-' + sid);
      if (!b) return;
      b.classList.toggle('open');
      if (b.classList.contains('open')) {
        setTimeout(function () {
          var cardsEl = b.querySelector('.bb-cards');
          if (cardsEl) cardsEl.dispatchEvent(new Event('scroll'));
        }, 50);
      }
    }

    function simplify(label) { return label.replace(/^choose your\s+/i, ''); }

    function labelToKey(label) {
      var s = simplify(label);
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
  }

  /* ─────────────────────────────────────────
     Auto-init
  ───────────────────────────────────────── */

  function init() {
    document.querySelectorAll('[data-section-id][id^="bb-"]').forEach(function (el) {
      if (!el.dataset.bbInit) { el.dataset.bbInit = '1'; new BundleBuilder(el); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector('[id^="bb-"][data-section-id]');
    if (el && !el.dataset.bbInit) { el.dataset.bbInit = '1'; new BundleBuilder(el); }
  });

})();